import React from 'react'

import { useIncidentSummary } from '../hooks/useIncidentSummary'
import { useLightMode } from '@/hooks/useLightMode'
import * as Sentry from '@sentry/nextjs'
import { useTranslation } from 'react-i18next'

import {
  Badge,
  Box,
  Divider,
  HStack,
  Icon,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalOverlay,
  Text,
  VStack,
  Wrap,
  WrapItem,
} from '@chakra-ui/react'

import { AiOperatorIcon } from '@hivewatch/dark'

import { Modal } from '@/components/overlays'
import { Button } from '@/components/ui'
import { AiIncidentSeverityCategory } from '@/graphql/generated/schemas'
import { formatDateTime } from '@/utils/formatDateTime'
import { tVar } from '@/utils/i18n'

import {
  CATEGORY_LABEL_KEYS,
  SEVERITY_FIELD_TO_CATEGORY,
  SummarizeIncidentsModalIProps,
  SummaryPayload,
} from '../utils/incidentSummary'

/**
 * Summarize modal for the legacy incidents table. The rebranded table renders
 * {@link ../components/DarkSummarizeIncidentsModal.DarkSummarizeIncidentsModal} instead, so this
 * one stays on the legacy Chakra primitives.
 *
 * It is built on Chakra v2 while the design system's dark tokens live in the Chakra v3
 * `@hivewatch/dark` provider, so the token values are mirrored here rather than read. Dark values
 * track `bg.card-elevated` (darkBlue.500), `text.primary` (darkBlue.50), `text.input-value`
 * (darkBlue.200) and `text.secondary` (darkBlue.300).
 */
const modalPalette = (isLight: boolean) => ({
  surface: isLight ? 'white' : '#002233',
  title: isLight ? 'brand.400' : '#e6eaed',
  subtitle: isLight ? 'brand.500' : '#e6eaed',
  body: isLight ? 'gray.700' : '#e6eaed',
  muted: isLight ? 'gray.500' : '#80949f',
  mutedStrong: isLight ? 'gray.600' : '#80949f',
  border: isLight ? 'gray.200' : '#4d6978',
  closeButton: isLight ? 'black' : '#e6eaed',
  accent: isLight ? 'brand.400' : '#e6eaed',
})

const CATEGORY_COLOR: Record<AiIncidentSeverityCategory, string> = {
  [AiIncidentSeverityCategory.Critical]: 'red',
  [AiIncidentSeverityCategory.Urgent]: 'orange',
  [AiIncidentSeverityCategory.Moderate]: 'yellow',
  [AiIncidentSeverityCategory.Low]: 'blue',
  [AiIncidentSeverityCategory.Informational]: 'gray',
}

export const SummarizeIncidentsModal = ({
  incidents,
  isOpen,
  onClose,
}: SummarizeIncidentsModalIProps) => {
  const { t } = useTranslation()
  const { summaryData, isLoading, handleSummarize, handleClose } =
    useIncidentSummary({ incidents, onClose })

  const colorScheme = useLightMode()
  const palette = modalPalette(colorScheme === 'light')

  return (
    <Modal
      closeOnEsc={false}
      closeOnOverlayClick={false}
      id='summarizeIncidentsModal'
      isCentered
      isOpen={isOpen}
      motionPreset='slideInBottom'
      onClose={handleClose}
      scrollBehavior='inside'
      size='4xl'
    >
      <ModalOverlay />
      <ModalContent bg={palette.surface} rounded='20px'>
        <ModalCloseButton
          color={palette.closeButton}
          isDisabled={isLoading}
          opacity={0.4}
        />

        <Box px='10' py='6'>
          <HStack spacing={2}>
            <Icon as={AiOperatorIcon} boxSize={6} color={palette.title} />
            <Box
              color={palette.title}
              fontSize='26px'
              fontWeight='extrabold'
              letterSpacing='-0.4px'
              lineHeight='1'
            >
              {t('incidents:summary_title')}
            </Box>
          </HStack>
        </Box>

        <ModalBody pb='4' px='10'>
          {summaryData ? (
            <SummaryResults data={summaryData} palette={palette} />
          ) : (
            <ConfirmationView incidents={incidents} palette={palette} />
          )}
        </ModalBody>

        <ModalFooter py='4'>
          {summaryData ? (
            <Button onClick={handleClose} variant='primary'>
              {t('common:close')}
            </Button>
          ) : (
            <>
              <Button
                isDisabled={isLoading}
                mr='3'
                onClick={handleClose}
                variant='secondary'
              >
                {t('common:cancel')}
              </Button>
              <Button
                isLoading={isLoading}
                loadingText={t('incidents:summary_generating')}
                onClick={handleSummarize}
              >
                {t('incidents:summarize')}
              </Button>
            </>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

const ConfirmationView = ({
  incidents,
  palette,
}: {
  incidents: Array<{ id: string; displayId: string }>
  palette: ReturnType<typeof modalPalette>
}) => {
  const { t } = useTranslation()
  return (
    <VStack align='start' spacing={3}>
      <Text color={palette.subtitle} fontSize='17px' letterSpacing='-0.4px'>
        {tVar(t, 'incidents:summary_confirm', { count: incidents.length })}
      </Text>
      <Box
        borderColor={palette.border}
        borderRadius='md'
        borderWidth={1}
        maxH='200px'
        overflowY='auto'
        px={4}
        py={3}
        w='full'
      >
        {/* Chakra v2 `Stack` spaces its children with margins, so a wrapping `HStack` leaves no
            gap between rows — `Wrap` spaces both axes. */}
        <Wrap spacing={2}>
          {incidents.map((inc) => (
            <WrapItem key={inc.id}>
              <Badge colorScheme='blue' fontSize='xs'>
                {inc.displayId}
              </Badge>
            </WrapItem>
          ))}
        </Wrap>
      </Box>
    </VStack>
  )
}

const SummaryResults = ({
  data,
  palette,
}: {
  data: SummaryPayload
  palette: ReturnType<typeof modalPalette>
}) => {
  const { t } = useTranslation()
  const { overview, categoryBreakdown, incidents, patterns } = data

  return (
    <VStack align='start' spacing={5}>
      {/* Overview */}
      <Box w='full'>
        <SectionTitle palette={palette}>
          {t('incidents:summary_section_overview')}
        </SectionTitle>
        <Text color={palette.body} fontSize='sm' mt={2}>
          {overview.narrative}
        </Text>
        <HStack mt={3} spacing={6}>
          <StatItem
            label={t('incidents:summary_stat_total')}
            palette={palette}
            value={overview.incidentCount}
          />
          <StatItem
            label={t('incidents:feed_status_active')}
            palette={palette}
            value={overview.statusBreakdown.active}
          />
          <StatItem
            label={t('incidents:feed_status_in_progress')}
            palette={palette}
            value={overview.statusBreakdown.inProgress}
          />
          <StatItem
            label={t('incidents:feed_status_resolved')}
            palette={palette}
            value={overview.statusBreakdown.resolved}
          />
        </HStack>
        <Text color={palette.muted} fontSize='xs' mt={2}>
          {formatDateTime(overview.dateRange.from)} —{' '}
          {formatDateTime(overview.dateRange.to)}
        </Text>
        {overview.topFacilities.length > 0 && (
          <Text color={palette.muted} fontSize='xs' mt={1}>
            {tVar(t, 'incidents:summary_top_facilities', {
              facilities: overview.topFacilities.join(', '),
            })}
          </Text>
        )}
        {overview.topDevices.length > 0 && (
          <Text color={palette.muted} fontSize='xs' mt={1}>
            {tVar(t, 'incidents:summary_top_devices', {
              devices: overview.topDevices.join(', '),
            })}
          </Text>
        )}
      </Box>

      <Divider />

      {/* Severity breakdown */}
      <Box w='full'>
        <SectionTitle palette={palette}>
          {t('incidents:summary_section_severity')}
        </SectionTitle>
        <HStack mt={3} spacing={3}>
          {Object.entries(overview.severityBreakdown)
            .filter(([key]) => key !== '__typename')
            .map(([key, count]) => {
              const category = SEVERITY_FIELD_TO_CATEGORY[key]
              if (!category) {
                // Surfaces backend schema drift rather than silently rendering a raw field name.
                Sentry.captureMessage(
                  `Unrecognized severityBreakdown field: ${key}`
                )
              }
              return (
                <Badge
                  colorScheme={CATEGORY_COLOR[category] ?? 'gray'}
                  fontSize='xs'
                  key={key}
                  px={2}
                  py={1}
                >
                  {category ? t(CATEGORY_LABEL_KEYS[category]) : key}: {count}
                </Badge>
              )
            })}
        </HStack>
      </Box>

      {/* Category breakdown */}
      {categoryBreakdown.length > 0 && (
        <>
          <Divider />
          <Box w='full'>
            <SectionTitle palette={palette}>
              {t('incidents:summary_section_category')}
            </SectionTitle>
            <VStack align='start' mt={2} spacing={2}>
              {categoryBreakdown.map((cb) => (
                <Box key={cb.category} w='full'>
                  <HStack>
                    <Badge
                      colorScheme={CATEGORY_COLOR[cb.category] ?? 'gray'}
                      fontSize='xs'
                    >
                      {t(CATEGORY_LABEL_KEYS[cb.category]) ?? cb.category}
                    </Badge>
                    <Text color={palette.mutedStrong} fontSize='xs'>
                      {cb.count} ({Math.round(cb.percentage)}%)
                    </Text>
                  </HStack>
                  {cb.narrative && (
                    <Text
                      color={palette.mutedStrong}
                      fontSize='xs'
                      mt={1}
                      pl={1}
                    >
                      {cb.narrative}
                    </Text>
                  )}
                </Box>
              ))}
            </VStack>
          </Box>
        </>
      )}

      {/* Patterns */}
      {patterns.length > 0 && (
        <>
          <Divider />
          <Box w='full'>
            <SectionTitle palette={palette}>
              {t('incidents:summary_section_patterns')}
            </SectionTitle>
            <VStack align='start' mt={2} spacing={2}>
              {patterns.map((p) => (
                <HStack align='start' key={p.description} spacing={2}>
                  <Text color={palette.accent} fontSize='sm' fontWeight='bold'>
                    ·
                  </Text>
                  <Text color={palette.body} fontSize='sm'>
                    {p.description}
                  </Text>
                </HStack>
              ))}
            </VStack>
          </Box>
        </>
      )}

      {/* Per-incident summaries */}
      {incidents.length > 0 && (
        <>
          <Divider />
          <Box w='full'>
            <SectionTitle palette={palette}>
              {t('incidents:summary_section_incidents')}
            </SectionTitle>
            <VStack align='start' mt={2} spacing={3}>
              {incidents.map((inc) => (
                <Box key={inc.incidentId} w='full'>
                  <HStack mb={1} spacing={2}>
                    <Text
                      color={palette.accent}
                      fontSize='sm'
                      fontWeight='semibold'
                    >
                      {inc.displayId}
                    </Text>
                    <Badge
                      colorScheme={
                        CATEGORY_COLOR[inc.assignedCategory] ?? 'gray'
                      }
                      fontSize='xs'
                    >
                      {t(CATEGORY_LABEL_KEYS[inc.assignedCategory]) ??
                        inc.assignedCategory}
                    </Badge>
                  </HStack>
                  <Text color={palette.body} fontSize='sm'>
                    {inc.summary}
                  </Text>
                </Box>
              ))}
            </VStack>
          </Box>
        </>
      )}
    </VStack>
  )
}

const SectionTitle = ({
  children,
  palette,
}: {
  children: React.ReactNode
  palette: ReturnType<typeof modalPalette>
}) => (
  <Text color={palette.subtitle} fontSize='md' fontWeight='semibold'>
    {children}
  </Text>
)

const StatItem = ({
  label,
  value,
  palette,
}: {
  label: string
  value: number
  palette: ReturnType<typeof modalPalette>
}) => (
  <VStack spacing={0}>
    <Text color={palette.accent} fontSize='lg' fontWeight='bold' lineHeight='1'>
      {value}
    </Text>
    <Text color={palette.muted} fontSize='xs'>
      {label}
    </Text>
  </VStack>
)
